/**
 * Resolves image URLs to ensure they work correctly.
 * Uses the backend URL for API routes and relative URLs when possible.
 * - If the URL starts with http:// or https://, use as-is (external images)
 * - If the URL starts with /api/, use backend URL (API routes)
 * - Otherwise, convert to /api/uploads/ path
 */
export const getImageUrl = (path) => {
  if (!path) return null;
  
  // Already an absolute URL (external images like unsplash)
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Handle paths - ensure they start with /api/uploads/
  let cleanPath = path;
  
  // If path starts with /uploads/, convert to /api/uploads/
  if (path.startsWith('/uploads/')) {
    cleanPath = '/api' + path;
  }
  // If path doesn't start with /, add /api/uploads/
  else if (!path.startsWith('/')) {
    cleanPath = '/api/uploads/' + path;
  }
  // If path starts with /api/, use as-is
  else if (!path.startsWith('/api/')) {
    cleanPath = '/api/uploads' + path;
  }
  
  // Use REACT_APP_BACKEND_URL for API routes, fallback to window.location.origin
  const origin = window.location.origin;
  let fullUrl = `${origin}${cleanPath}`;
  
  // Add cache-busting if not already present
  if (!fullUrl.includes('?v=') && !fullUrl.includes('&v=') && !fullUrl.includes('_t=') && !fullUrl.includes('_cb=')) {
    const separator = fullUrl.includes('?') ? '&' : '?';
    fullUrl = `${fullUrl}${separator}_cb=${Date.now()}`;
  }
  
  return fullUrl;
};

/**
 * Get thumbnail URL from main image URL.
 * Thumbnails are smaller versions for dashboard views to save bandwidth.
 * - Converts filename.webp to filename_thumb.webp
 * - Preserves cache-busting query params
 */
export const getThumbnailUrl = (path) => {
  if (!path) return null;
  
  // External URLs don't have thumbnails
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return getImageUrl(path);
  }
  
  // Split path and query params
  const [basePath, queryParams] = path.split('?');
  
  // Convert filename.webp to filename_thumb.webp
  let thumbPath = basePath;
  if (basePath.endsWith('.webp')) {
    thumbPath = basePath.replace('.webp', '_thumb.webp');
  }
  
  // Reconstruct with query params
  if (queryParams) {
    thumbPath = `${thumbPath}?${queryParams}`;
  }
  
  return getImageUrl(thumbPath);
};

/**
 * Build a responsive srcset string for a stored Wayo image URL.
 *
 * Each upload generates four WebP variants in GridFS:
 *   - filename.webp           (main, up to 1920w banner / 800w logo)
 *   - filename_md.webp        (768w)
 *   - filename_sm.webp        (480w)
 *   - filename_thumb.webp     (200w)
 *
 * `sizes` should be supplied by the consumer to match the rendered layout
 * (e.g. for a 3-column card grid: "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw").
 *
 * Returns { src, srcSet } — falls back to a single `src` when given an
 * external URL (the variants only exist for GridFS-hosted images).
 */
export const buildResponsiveImage = (path, { fallbackWidth = 1920 } = {}) => {
  if (!path) return { src: null, srcSet: null };

  const src = getImageUrl(path);

  // External URLs (Unsplash etc.) → no variants available
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return { src, srcSet: null };
  }

  const [basePath, queryParams] = src.split('?');
  if (!basePath.endsWith('.webp')) {
    return { src, srcSet: null };
  }

  const stem = basePath.slice(0, -'.webp'.length);
  const qs = queryParams ? `?${queryParams}` : '';

  // Each entry: [suffix, width]. main image keeps the original width cap.
  const variants = [
    [`_sm.webp${qs}`, 480],
    [`_md.webp${qs}`, 768],
    [`.webp${qs}`, fallbackWidth],
  ];

  const srcSet = variants
    .map(([suffix, w]) => `${stem}${suffix} ${w}w`)
    .join(', ');

  return { src, srcSet };
};

/**
 * Add cache-busting parameter to image URL
 * Useful when updating images to force browser to fetch new version
 */
export const bustImageCache = (path) => {
  if (!path) return null;
  
  const timestamp = Date.now();
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}_t=${timestamp}`;
};
